// src/features/account/components/UsagePage.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Chip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Tooltip,
  IconButton,
  TablePagination,
  Tabs,
  Tab
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  Refresh as RefreshIcon,
  Info as InfoIcon,
  FilterList as FilterIcon,
  CloudDownload as DownloadIcon,
  Timeline as TimelineIcon,
  PieChart as PieChartIcon,
  TableChart as TableChartIcon
} from '@mui/icons-material';
import { format, parseISO, subDays, subMonths } from 'date-fns';
import { fetchUsageStats } from '../api/accountApi';
import useAccountStore from '../../../store/accountStore';

// Interface for the service breakdown data
interface ServiceBreakdownItem {
  name: string;
  value: number;
  cost: number;
}

// Interface for detailed usage data
interface DetailedUsageItem {
  id: string;
  timestamp: string;
  service: string;
  inputTokens: number;
  outputTokens: number;
  cost: string | number;
}

// Interface for PieChart label props
interface PieLabelProps {
  cx?: number;
  cy?: number;
  midAngle?: number;
  innerRadius?: number;
  outerRadius?: number;
  percent?: number;
  index?: number;
  name?: string;
  value?: number;
}

// Interface for tooltip props
interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<any>;
  label?: string;
}

// Custom tooltip component for charts
const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper elevation={3} sx={{ p: 2, bgcolor: 'background.paper' }}>
        <Typography variant="subtitle2" gutterBottom>{label}</Typography>
        {payload.map((entry: any, index: number) => (
          <Box key={`tooltip-${index}`} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box
              component="span"
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: entry.color,
                mr: 1
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {entry.name}: {entry.value} {entry.unit || ''}
            </Typography>
          </Box>
        ))}
      </Paper>
    );
  }
  return null;
};

// Interface for tab panel props
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab panel component
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`usage-tabpanel-${index}`}
      aria-labelledby={`usage-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Main Usage Page component
const UsagePage: React.FC = () => {
  const navigate = useNavigate();
  const { usage, isLoading, error } = useAccountStore();
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [timeRange, setTimeRange] = useState<string>('month');
  const [tabValue, setTabValue] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  
  // State with proper types
  const [detailedUsage, setDetailedUsage] = useState<DetailedUsageItem[]>([]);
  const [serviceBreakdown, setServiceBreakdown] = useState<ServiceBreakdownItem[]>([]);

  // Colors for pie chart
  const COLORS = ['#3f51b5', '#f50057', '#00bcd4', '#ff9800', '#4caf50', '#9c27b0'];

  // Fetch usage statistics
  useEffect(() => {
    const loadUsageData = async () => {
      try {
        await fetchUsageStats();
        console.log("Usage data loaded:", useAccountStore.getState().usage);
        
        // Generate sample detailed usage data - replace with actual API call
        generateMockData();
      } catch (err) {
        console.error("Error loading usage data:", err);
      }
    };
    
    loadUsageData();
  }, [timeRange]);

  // Generate mock data for demo purposes - replace with actual data in production
  const generateMockData = () => {
    // Sample detailed usage
    const now = new Date();
    const mockUsage: DetailedUsageItem[] = Array.from({ length: 50 }, (_, i) => {
      const date = subDays(now, i % 30);
      return {
        id: `req-${i}`,
        timestamp: date.toISOString(),
        service: ['anthropic-opus', 'openai-gpt4', 'anthropic-sonnet', 'google-gemini'][i % 4],
        inputTokens: Math.floor(Math.random() * 1000) + 200,
        outputTokens: Math.floor(Math.random() * 2000) + 100,
        cost: (Math.random() * 0.1).toFixed(4),
      };
    });
    
    // Sample service breakdown
    const serviceData: ServiceBreakdownItem[] = [
      { name: 'Anthropic Opus', value: 45, cost: 3.25 },
      { name: 'OpenAI GPT-4', value: 30, cost: 2.10 },
      { name: 'Anthropic Sonnet', value: 15, cost: 0.75 },
      { name: 'Google Gemini', value: 10, cost: 0.40 }
    ];
    
    setDetailedUsage(mockUsage);
    setServiceBreakdown(serviceData);
  };

  // Handle reload
  const handleReload = async () => {
    setRefreshing(true);
    try {
      await fetchUsageStats();
      generateMockData(); // Regenerate mock data
    } catch (err) {
      console.error("Error reloading usage data:", err);
    } finally {
      setRefreshing(false);
    }
  };

  // Handle time range change
  const handleTimeRangeChange = (event: SelectChangeEvent) => {
    setTimeRange(event.target.value);
  };

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    try {
      return format(new Date(timestamp), 'MMM dd, yyyy HH:mm');
    } catch (e) {
      return timestamp;
    }
  };

  // Process chart data from usage
  const getChartData = () => {
    if (usage?.dailyUsage && Array.isArray(usage.dailyUsage)) {
      try {
        return usage.dailyUsage.map(item => ({
          date: typeof item.date === 'string' ? format(new Date(item.date), 'MMM dd') : 'Invalid date',
          calls: typeof item.count === 'number' ? item.count : 0
        }));
      } catch (e) {
        console.error("Error processing chart data:", e);
        return [];
      }
    }
    return [];
  };

  // If loading and no usage data yet
  if (isLoading && !usage && !refreshing) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1">
          Usage Statistics
        </Typography>
        
        <Box display="flex" alignItems="center" gap={2}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="time-range-label">Time Range</InputLabel>
            <Select
              labelId="time-range-label"
              value={timeRange}
              label="Time Range"
              onChange={handleTimeRangeChange}
            >
              <MenuItem value="day">Last 24 Hours</MenuItem>
              <MenuItem value="week">Last 7 Days</MenuItem>
              <MenuItem value="month">Last 30 Days</MenuItem>
              <MenuItem value="year">Last 12 Months</MenuItem>
            </Select>
          </FormControl>
          
          <Button 
            variant="outlined" 
            startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={handleReload}
            disabled={isLoading || refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {typeof error === 'string' ? error : 'An error occurred while loading data'}
        </Alert>
      )}

      {/* Usage Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Total API Calls
              </Typography>
              <Typography variant="h4">
                {typeof usage?.totalUsage === 'number' ? usage.totalUsage.toLocaleString() : '0'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Total Cost
              </Typography>
              <Typography variant="h4">
                ${typeof usage?.totalCost === 'number' ? usage.totalCost.toFixed(2) : '0.00'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Available Balance
              </Typography>
              <Typography variant="h4" color="primary">
                ${typeof usage?.balance === 'number' ? usage.balance.toFixed(2) : '0.00'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Average Cost/Call
              </Typography>
              <Typography variant="h4">
                {usage && typeof usage.totalUsage === 'number' && usage.totalUsage > 0 
                  ? `$${(((usage.totalCost || 0) / usage.totalUsage)).toFixed(4)}` 
                  : '$0.0000'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs for different views */}
      <Paper sx={{ mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="usage tabs">
            <Tab 
              label="Usage Over Time" 
              icon={<TimelineIcon />} 
              iconPosition="start"
              id="usage-tab-0"
              aria-controls="usage-tabpanel-0"
            />
            <Tab 
              label="Service Breakdown" 
              icon={<PieChartIcon />} 
              iconPosition="start"
              id="usage-tab-1"
              aria-controls="usage-tabpanel-1"
            />
            <Tab 
              label="Detailed Usage" 
              icon={<TableChartIcon />} 
              iconPosition="start"
              id="usage-tab-2"
              aria-controls="usage-tabpanel-2"
            />
          </Tabs>
        </Box>

        {/* Usage Over Time */}
        <TabPanel value={tabValue} index={0}>
          <Box height={400} p={2}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={getChartData()}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 20,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: '#e0e0e0' }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: '#e0e0e0' }}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="calls"
                  stroke="#3f51b5"
                  strokeWidth={2}
                  activeDot={{ r: 8 }}
                  name="API Calls"
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </TabPanel>

        {/* Service Breakdown */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(2px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                borderRadius: 1
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'info.light',
                  color: 'info.contrastText',
                  border: '2px solid',
                  borderColor: 'info.main'
                }}
              >
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  🚧 Coming Soon
                </Typography>
                <Typography variant="body1">
                  Service breakdown analytics are currently in development.
                  This feature will be available in an upcoming release.
                </Typography>
              </Paper>
            </Box>
            <Grid container spacing={3} sx={{ opacity: 0.3 }}>
            <Grid item xs={12} md={6}>
              <Box height={400} p={2}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={serviceBreakdown}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }: PieLabelProps) => 
                        `${name || ''}: ${((percent || 0) * 100).toFixed(0)}%`
                      }
                      outerRadius={150}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                    >
                      {serviceBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Service Usage Breakdown</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Service</TableCell>
                      <TableCell align="right">Usage (%)</TableCell>
                      <TableCell align="right">Requests</TableCell>
                      <TableCell align="right">Cost ($)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {serviceBreakdown.map((service, index) => (
                      <TableRow key={`service-${index}`}>
                        <TableCell component="th" scope="row">
                          <Box display="flex" alignItems="center">
                            <Box
                              component="span"
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: COLORS[index % COLORS.length],
                                mr: 1
                              }}
                            />
                            {service.name}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{service.value}%</TableCell>
                        <TableCell align="right">
                          {Math.round((service.value * (usage?.totalUsage || 0)) / 100)}
                        </TableCell>
                        <TableCell align="right">${service.cost.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {usage?.totalUsage || 0}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        ${serviceBreakdown.reduce((sum, service) => sum + service.cost, 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
          </Box>
        </TabPanel>

        {/* Detailed Usage */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(2px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                borderRadius: 1
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'warning.light',
                  color: 'warning.contrastText',
                  border: '2px solid',
                  borderColor: 'warning.main'
                }}
              >
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  🗺️ Sample Data
                </Typography>
                <Typography variant="body1">
                  This detailed usage table shows sample data for demonstration.
                  Real usage tracking will be available in an upcoming release.
                </Typography>
              </Paper>
            </Box>
            <Box sx={{ opacity: 0.3 }}>
          <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Request History</Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              size="small"
              onClick={() => console.log('Download CSV')}
            >
              Export CSV
            </Button>
          </Box>
          
          <TableContainer component={Paper} variant="outlined">
            <Table sx={{ minWidth: 650 }} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell align="right">Input Tokens</TableCell>
                  <TableCell align="right">Output Tokens</TableCell>
                  <TableCell align="right">Cost ($)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detailedUsage
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {formatTimestamp(row.timestamp)}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={row.service} 
                          size="small" 
                          color={
                            row.service.includes('anthropic') ? 'primary' :
                            row.service.includes('openai') ? 'secondary' :
                            'default'
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">{row.inputTokens.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.outputTokens.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        ${typeof row.cost === 'string' ? parseFloat(row.cost).toFixed(4) : row.cost.toFixed(4)}
                      </TableCell>
                    </TableRow>
                  ))}
                
                {detailedUsage.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        No usage data available for this period
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={detailedUsage.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          </Box>
          </Box>
        </TabPanel>
      </Paper>

      {/* Usage Metrics */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Cost Efficiency Metrics</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Average Cost per Request
              </Typography>
              <Typography variant="h5" color="primary">
                {usage && typeof usage.totalUsage === 'number' && usage.totalUsage > 0 
                  ? `$${(((usage.totalCost || 0) / usage.totalUsage)).toFixed(4)}` 
                  : '$0.0000'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {usage && typeof usage.totalUsage === 'number' && usage.totalUsage > 0 && 
                 typeof usage.totalCost === 'number' && (usage.totalCost / usage.totalUsage) < 0.01 
                  ? 'Good efficiency' 
                  : 'Consider optimizing requests'}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Input/Output Token Ratio
              </Typography>
              <Typography variant="h5" color="primary">
                1:{detailedUsage.length > 0 
                  ? (detailedUsage.reduce((sum, item) => sum + item.outputTokens, 0) / 
                    detailedUsage.reduce((sum, item) => sum + item.inputTokens, 0)).toFixed(1)
                  : '0.0'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Higher values indicate more output per input
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Daily Average Usage
              </Typography>
              <Typography variant="h5" color="primary">
                {usage && usage.dailyUsage && usage.dailyUsage.length > 0
                  ? (usage.dailyUsage.reduce((sum, day) => sum + (day.count || 0), 0) / usage.dailyUsage.length).toFixed(1)
                  : '0.0'} requests
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Based on the current period
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Recommendations */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Usage Recommendations</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Optimize Input Tokens</Typography>
              <Typography variant="body2">
                You can reduce costs by being more concise in your prompts. Each input token costs money, so use them efficiently.
              </Typography>
            </Alert>
          </Grid>
          
          <Grid item xs={12}>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Consider Using Lower-Cost Models</Typography>
              <Typography variant="body2">
                For tasks that don't require the highest capabilities, using models like Haiku or Sonnet instead of Opus can significantly reduce costs.
              </Typography>
            </Alert>
          </Grid>
          
          <Grid item xs={12}>
            <Alert severity="warning">
              <Typography variant="subtitle2">Add Credit to Your Account</Typography>
              <Typography variant="body2">
                Your current balance is ${typeof usage?.balance === 'number' ? usage.balance.toFixed(2) : '0.00'}. Consider adding more credit to ensure uninterrupted service.
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                color="inherit" 
                onClick={() => navigate('/payment')}
                sx={{ mt: 1 }}
              >
                Add Credit
              </Button>
            </Alert>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default UsagePage;